import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import "./AssignCgtTopic.css";

const API_BASE = "https://payyug.in/api";

const AssignCgtTopic = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 14;
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [cgts, setCgts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [reports, setReports] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCgt, setSelectedCgt] = useState(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);

  const [loadingReports, setLoadingReports] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);

  // fetch assign topics
  const fetchReportList = async () => {
    if (!selectedCgt?.id || !selectedGroup?.id) {
      setReports([]);
      return;
    }
    setLoadingReports(true);
    try {
      const res = await axios.get(`${API_BASE}/assigned-topics/all`, {
        params: {
          cgtId: selectedCgt.id,
          groupId: selectedGroup.id,
          superAdminId,
        },
      });
      setReports(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load records:", err);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (superAdminId) {
      fetchReportList();
    }
  }, [superAdminId, selectedGroup, selectedCgt]);

  useEffect(() => {
    if (reports.length > 0) {
      const assignedIds = reports
        .map((item) => Number(item.topic?.id || item.topicId))
        .filter((id) => !isNaN(id) && id > 0);

      setSelectedTopicIds(assignedIds);
    } else {
      setSelectedTopicIds([]);
    }
  }, [reports]);

  // initial loads
  useEffect(() => {
    const initData = async () => {
      try {
        const [brRes, topRes] = await Promise.all([
          axios.get(`${API_BASE}/get-branch`, {
            params: { SuperAdminId: superAdminId, limit: "all" },
          }),
          axios.get(`${API_BASE}/cgt-topic/list`, {
            params: { superAdminId, limit: 100000 },
          }),
        ]);
        setBranches(brRes?.data?.branches || []);
        setTopics(topRes.data?.data || []);
      } catch (err) {
        console.error("Initialization error:", err);
      }
    };
    if (superAdminId) initData();
  }, [superAdminId]);

  // Fetch Centers when Branch changes
  useEffect(() => {
    if (!selectedBranch?.id) return setCenters([]);
    axios
      .get(`${API_BASE}/group/centre`, {
        params: { superAdminId, branchid: selectedBranch.id, limit: 100000 },
      })
      .then((res) => setCenters(res?.data?.data || []))
      .catch((err) => console.error(err));
  }, [superAdminId, selectedBranch]);

  // Fetch Groups when Center changes
  useEffect(() => {
    if (!selectedCenter?.id) return setGroups([]);
    axios
      .get(`${API_BASE}/groupupdate/getall`, {
        params: { superAdminId, CenterId: selectedCenter.id, limit: 100000 },
      })
      .then((res) => setGroups(res.data?.data || []))
      .catch((err) => console.error(err));
  }, [superAdminId, selectedCenter]);

  // Fetch CGTs when Group changes
  useEffect(() => {
    if (!selectedBranch?.id || !selectedCenter?.id || !selectedGroup?.id) {
      setCgts([]);
      setSelectedCgt(null);
      return;
    }
    axios
      .get(`${API_BASE}/cgt/list`, {
        params: {
          superAdminId,
          BranchId: selectedBranch.id,
          CenterId: selectedCenter.id,
          groupId: selectedGroup.id,
          limit: 100000,
        },
      })
      .then((res) => {
        const fetchedCgts = res.data?.data || res.data?.cgts || [];
        setCgts(fetchedCgts);
        if (fetchedCgts.length === 0) {
          setSelectedCgt(null);
          setReports([]);
        }
      })
      .catch((err) => console.error(err));
  }, [superAdminId, selectedBranch, selectedCenter, selectedGroup]);

  const handleBranchChange = (e) => {
    const brId = Number(e.target.value);
    setSelectedBranch(branches.find((b) => b.id === brId) || null);
    setSelectedCenter(null);
    setSelectedGroup(null);
    setSelectedCgt(null);
    setReports([]);
    setCurrentPage(1);
  };

  const handleCenterChange = (e) => {
    const cId = Number(e.target.value);
    setSelectedCenter(centers.find((c) => c.id === cId) || null);
    setSelectedGroup(null);
    setSelectedCgt(null);
    setReports([]);
    setCurrentPage(1);
  };

  const handleGroupChange = (e) => {
    const gId = Number(e.target.value);
    setSelectedGroup(groups.find((g) => g.id === gId) || null);
    setSelectedCgt(null);
    setReports([]);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (topicId) => {
    const id = Number(topicId);
    setSelectedTopicIds((prev) =>
      prev.includes(id) ? prev.filter((tId) => tId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    setSelectedTopicIds(
      selectedTopicIds.length === topics.length
        ? []
        : topics.map((t) => Number(t.id)),
    );
  };

  const resetFormState = () => {
    setSelectedBranch(null);
    setSelectedCenter(null);
    setSelectedGroup(null);
    setSelectedCgt(null);
    setSelectedTopicIds([]);
    setReports([]);
  };

  // handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !selectedBranch?.id ||
      !selectedCenter?.id ||
      !selectedGroup?.id ||
      !selectedCgt?.id
    ) {
      return toast.warn("Please complete all dropdown selections.");
    }
    if (selectedTopicIds.length === 0) {
      return toast.warn("Please select at least one topic.");
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/assign-topic`, {
        cgtId: Number(selectedCgt.id),
        groupId: Number(selectedGroup.id),
        topics: selectedTopicIds,
        employeId: selectedCgt.employeId ? Number(selectedCgt.employeId) : 5,
        superAdminId: Number(superAdminId),
        BranchId: Number(selectedBranch.id),
        CenterId: Number(selectedCenter.id),
        createdBy: superAdminId,
      });
      toast.success(res?.data?.message || "Topics successfully assigned!");
      resetFormState();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Assignment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteAssignment = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to mark this CGT process as Completed?",
      )
    )
      return;

    setCompleting(true);
    try {
      const res = await axios.put(
        `${API_BASE}/assigned-topics/${id}/complete`,
        {
          id,
          updateBy: Number(superAdminId),
        },
      );

      toast.success(
        res?.data?.message || "Process marked as completed successfully!",
      );
      fetchReportList();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to complete the task.",
      );
    } finally {
      setCompleting(false);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const res = await axios.delete(`${API_BASE}/assigned-topics/${id}`);
      toast.success(res?.data?.message || "Record deleted clean.");
      setReports((prevReports) =>
        prevReports.filter((report) => report.id !== id),
      );
      fetchReportList();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  const filteredReports = reports.filter((item) => {
    const q = searchQuery.toLowerCase();
    return (
      item.topic?.topicName?.toLowerCase().includes(q) ||
      item.topic?.description?.toLowerCase().includes(q)
    );
  });

  const totalItems = filteredReports.length;
  const parsedSize = itemsPerPage === "all" ? totalItems : Number(itemsPerPage);
  const totalPages = Math.ceil(totalItems / parsedSize) || 1;
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * parsedSize,
    currentPage * parsedSize,
  );

  return (
    <div className="assign-cgt-container" ref={formRef}>
      <div className="assign-cgt-wrapper">
        {/* Header */}
        <div className="assign-cgt-top-header">
          <ArrowLeftIcon
            className="assign-cgt-top-header-icon"
            onClick={() => navigate(-1)}
          />
          <h2 className="assign-cgt-top-header-title">Assign CGT Topic</h2>
          <ArrowRightIcon
            className="assign-cgt-top-header-icon"
            onClick={() => navigate(1)}
          />
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="assign-cgt-cards">
          <div className="assign-cgt-selection-inputs-grid">
            {/* Branch Dropdown */}
            <div className="assign-cgt-form-group">
              <label>
                Select Branch{" "}
                <span className="assign-cgt-required-field">*</span>
              </label>
              <select
                value={selectedBranch?.id || ""}
                onChange={handleBranchChange}
              >
                <option value="">-- Choose Branch --</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Center Dropdown */}
            <div className="assign-cgt-form-group">
              <label>
                Select Center{" "}
                <span className="assign-cgt-required-field">*</span>
              </label>
              <select
                value={selectedCenter?.id || ""}
                disabled={!selectedBranch}
                onChange={handleCenterChange}
              >
                <option value="">-- Choose Center --</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.CenterName}
                  </option>
                ))}
              </select>
            </div>

            {/* Group Dropdown */}
            <div className="assign-cgt-form-group">
              <label>
                Select Group{" "}
                <span className="assign-cgt-required-field">*</span>
              </label>
              <select
                value={selectedGroup?.id || ""}
                disabled={!selectedCenter}
                onChange={handleGroupChange}
              >
                <option value="">-- Choose Group --</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupname}
                  </option>
                ))}
              </select>
            </div>

            {/* CGT Dropdown */}
            <div className="assign-cgt-form-group">
              <label>
                Select CGT <span className="assign-cgt-required-field">*</span>
              </label>
              <select
                value={selectedCgt?.id || ""}
                disabled={!selectedGroup || cgts.length === 0}
                onChange={(e) =>
                  setSelectedCgt(
                    cgts.find((x) => x.id === Number(e.target.value)) || null,
                  )
                }
              >
                <option value="">
                  {cgts.length === 0
                    ? "-- No CGT Available --"
                    : "-- Choose CGT --"}
                </option>
                {cgts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.Description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Topics Checklist Section */}
          <div className="assign-cgt-topics-section">
            <div className="assign-cgt-topics-header">
              <h3 className="assign-cgt-topics-section-title">
                CGT Topics Checklist
              </h3>
              {topics.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="assign-cgt-topics-select-all-btn"
                >
                  {selectedTopicIds.length === topics.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>

            <div className="assign-cgt-topics-list-group">
              {topics.length === 0 ? (
                <div className="assign-cgt-topics-empty-message">
                  No active CGT topics found on server.
                </div>
              ) : (
                topics.map((topic) => {
                  const isChecked = selectedTopicIds.includes(Number(topic.id));
                  return (
                    <label
                      key={topic.id}
                      className={`assign-cgt-topics-list-item ${isChecked ? "assign-cgt-topics-active-row" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCheckboxChange(topic.id)}
                        className="assign-cgt-topics-checkbox"
                      />
                      <div>
                        <span className="assign-cgt-topic-name">
                          {topic.topicName}
                        </span>
                        {topic.description && (
                          <span className="assign-cgt-topic-desc">
                            {topic.description}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="assign-cgt-form-footer">
            <button
              type="submit"
              disabled={submitting}
              className="assign-cgt-form-submit-btn"
            >
              {submitting ? "Processing..." : "Assign Topics"}
            </button>
          </div>
        </form>

        {/* Report List Section Table */}
        <div className="cgt-topic-section">
          <div
            className="cgt-report-card-top-bar"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <h3 style={{ margin: 0 }}>CGT Topics Report List</h3>
            {/* <button
              type="button"
              onClick={handleCompleteAssignment}
              disabled={completing || !selectedCgt}
              className="cgt-action-btn-complete"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: selectedCgt ? "#22c55e" : "#cbd5e1",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: selectedCgt ? "pointer" : "not-allowed",
                fontWeight: "bold",
                transition: "background-color 0.2s",
              }}
            >
              <CheckCircle size={16} />
              {completing ? "Completing..." : "Complete"}
            </button> */}
          </div>

          <div className="cgt-topic-report-filter">
            <input
              type="text"
              placeholder="Search by Branch, Center, Group..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="cgt-topic-report-search-input"
            />

            <div className="cgt-topic-report-limit-container">
              <label>Show Entries:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(e.target.value);
                  setCurrentPage(1);
                }}
                className="cgt-topic-report-limit-select"
              >
                <option value="10">10 rows</option>
                <option value="25">25 rows</option>
                <option value="50">50 rows</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>

          <div className="cgt-page-table-responsive">
            <table className="cgt-page-table">
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Center</th>
                  <th>Group</th>
                  <th>Assigned Topics</th>
                  <th>Assigned Date & Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingReports ? (
                  <tr>
                    <td colSpan="6" className="cgt-table-empty">
                      Loading records from server...
                    </td>
                  </tr>
                ) : paginatedReports.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="cgt-table-empty">
                      No Records Found.
                    </td>
                  </tr>
                ) : (
                  paginatedReports.map((report) => {
                    const isCompleted = report.isCompleted === true;
                    return (
                      <tr key={report.id}>
                        <td className="cgt-bold-cell">{report.BranchId}</td>
                        <td>{report.CenterId}</td>
                        <td>
                          <span className="cgt-badge-group">
                            {report.groupId}
                          </span>
                        </td>
                        <td>
                          <div>{report.topic?.topicName}</div>
                        </td>
                        <td className="cgt-timestamp">
                          {report.topic?.description || "N/A"}
                        </td>
                        <td style={{display:"flex",gap:"5px"}}>
                          <button
                            type="button"
                            onClick={() => handleCompleteAssignment(report.id)}
                            style={{
                              backgroundColor: isCompleted
                              ? "#afebd2"
                                : "#22c55e",
                              color: "#fff",
                              border: "none",
                              padding: "8px 16px",
                              borderRadius: "6px",
                              cursor: isCompleted ? "not-allowed" : "pointer",
                              fontWeight: "bold",
                              transition: "background-color 0.2s",
                            }}
                          >
                            {isCompleted
                              ? "Completed"
                              : "Complete"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAssignment(report.id)}
                            className="cgt-action-btn-del"
                            
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {itemsPerPage !== "all" && totalPages > 1 && (
            <div className="cgt-topic-pagination-container">
              <span className="cgt-topic-pagination-stats">
                Showing Page <strong>{currentPage}</strong> of{" "}
                <strong>{totalPages}</strong> ({totalItems} items total)
              </span>
              <div className="cgt-topic-pagination-buttons">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1 || loadingReports}
                  className="cgt-topic-pagination-btn"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || loadingReports}
                  className="cgt-topic-pagination-btn"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignCgtTopic;
